import {
  BOOLEAN_UNIVERSE,
  EMPTY_FILTER_VALUE,
  type FilterDataType,
  type FilterOperator,
  getDefaultOperator,
  isNegationOperator,
  operatorValueCount,
} from 'admin/src/components/ui/DataGrid/filters/operators';
import type { Condition, FilterModel } from 'admin/src/types/Filter';
import dayjs from 'dayjs';

export { EMPTY_FILTER_VALUE };
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_FORMAT_USER = 'MMM D, YYYY';

const NUMBER_TYPE_BY_OPERATOR: Partial<Record<FilterOperator, string>> = {
  is: 'equals',
  'is not': 'notEqual',
  'is greater than': 'greaterThan',
  'is greater than or equal to': 'greaterThanOrEqual',
  'is less than': 'lessThan',
  'is less than or equal to': 'lessThanOrEqual',
  'is between': 'inRange',
};

const DATE_TYPE_BY_OPERATOR: Partial<Record<FilterOperator, string>> = {
  is: 'equals',
  'is not': 'notEqual',
  'is before': 'lessThan',
  'is after': 'greaterThan',
  'is between': 'inRange',
};

function invert(map: Partial<Record<FilterOperator, string>>): Record<string, FilterOperator> {
  return Object.entries(map).reduce<Record<string, FilterOperator>>((acc, [operator, type]) => {
    if (type) acc[type] = operator as FilterOperator;
    return acc;
  }, {});
}

const NUMBER_OPERATOR_BY_TYPE = invert(NUMBER_TYPE_BY_OPERATOR);
const DATE_OPERATOR_BY_TYPE = invert(DATE_TYPE_BY_OPERATOR);

export type AgGridTextCondition = {
  filterType: 'text';
  type: string;
  filter?: string | null;
};

/**
 * What AG Grid actually accepts, which `FilterModel` describes only loosely: set values may be
 * null for the Empty pseudo-option, number bounds are numbers, and the upper bound is nullable.
 */
export type AgGridColumnFilterModel = {
  filterType: 'set' | 'number' | 'date' | 'text';
  type?: string;
  values?: (string | null)[];
  filter?: string | number | null;
  filterTo?: number | null;
  dateFrom?: string;
  dateTo?: string | null;
  operator?: 'AND' | 'OR';
  conditions?: AgGridTextCondition[];
};

/** The same model widened for reading, since `getFilterModel` returns nulls the type omits. */
export type ReadableFilterModel = Omit<FilterModel, 'values' | 'filter' | 'filterTo' | 'dateTo' | 'conditions'> & {
  values?: (string | number | boolean | Date | null)[];
  filter?: string | number | null;
  filterTo?: string | number | null;
  dateTo?: string | null;
  conditions?: Condition[];
};

export type ParsedFilter = {
  operator: FilterOperator;
  values: string[];
};

function isFilledNumber(value: string | undefined): boolean {
  if (value === undefined || value.trim() === '') return false;
  return Number.isFinite(Number(value));
}

function textConditionForValue(negative: boolean, value: string): AgGridTextCondition {
  if (value === EMPTY_FILTER_VALUE) {
    return { filterType: 'text', type: negative ? 'notBlank' : 'blank' };
  }

  return {
    filterType: 'text',
    type: negative ? 'notEqual' : 'equals',
    filter: value,
  };
}

function toTextModel(operator: FilterOperator, values: string[]): AgGridColumnFilterModel | null {
  if (!values.length) return null;

  const negative = isNegationOperator(operator);

  if (values.length === 1) {
    return textConditionForValue(negative, values[0]);
  }

  return {
    filterType: 'text',
    operator: negative ? 'AND' : 'OR',
    conditions: values.map((value) => textConditionForValue(negative, value)),
  };
}

function valueFromTextCondition(condition: { type?: string | null; filter?: string | number | null }): string | null {
  if (condition.type === 'blank') return EMPTY_FILTER_VALUE;
  if (condition.type === 'notBlank') return EMPTY_FILTER_VALUE;
  if (condition.filter === null || condition.filter === undefined) return null;
  return String(condition.filter);
}

function fromTextModel(model: ReadableFilterModel): ParsedFilter | null {
  if (Array.isArray(model.conditions) && model.conditions.length) {
    const negative = model.operator === 'AND' || model.conditions.every((condition) => condition.type === 'notEqual' || condition.type === 'notBlank');
    const values = model.conditions.map((condition) => valueFromTextCondition(condition)).filter((value): value is string => value !== null);

    if (!values.length) return null;

    return {
      operator: negative ? (values.length > 1 ? 'not any of' : 'is not') : values.length > 1 ? 'is any of' : 'is',
      values,
    };
  }

  if (model.type === 'blank') return { operator: 'is', values: [EMPTY_FILTER_VALUE] };
  if (model.type === 'notBlank') return { operator: 'is not', values: [EMPTY_FILTER_VALUE] };
  if (model.type === 'equals' && model.filter !== null && model.filter !== undefined) {
    return { operator: 'is', values: [String(model.filter)] };
  }
  if (model.type === 'notEqual' && model.filter !== null && model.filter !== undefined) {
    return { operator: 'is not', values: [String(model.filter)] };
  }

  return null;
}

function fromLegacyTextSetModel(model: ReadableFilterModel): ParsedFilter | null {
  if (!Array.isArray(model.values)) return null;

  const values = model.values.map((value) => (value === null || value === undefined ? EMPTY_FILTER_VALUE : String(value)));
  if (!values.length) return null;

  return {
    operator: values.length > 1 ? 'is any of' : 'is',
    values,
  };
}

/**
 * Builds the AG Grid column filter model for an operator and its values. Returns null when
 * the selection is incomplete, so callers can skip writing rather than apply a partial filter.
 *
 * Text uses raw Text Filter models (`equals` / `notEqual` / combined). Boolean "is not" stays a
 * complementary set, since the set filter has no native exclusion.
 */
export function toAgGridModel(dataType: FilterDataType, operator: FilterOperator, values: string[]): AgGridColumnFilterModel | null {
  if (dataType === 'boolean') {
    if (!values.length) return null;

    const selected = operator === 'is not' ? BOOLEAN_UNIVERSE.filter((value) => !values.includes(value)) : values;

    if (!selected.length) return null;

    return {
      filterType: 'set',
      values: selected.map((value) => (value === EMPTY_FILTER_VALUE ? null : value)),
    };
  }

  if (dataType === 'text') {
    return toTextModel(operator, values);
  }

  const needed = operatorValueCount(dataType, operator);

  if (dataType === 'number') {
    const type = NUMBER_TYPE_BY_OPERATOR[operator];
    if (!type) return null;
    if (!isFilledNumber(values[0])) return null;
    if (needed === 2 && !isFilledNumber(values[1])) return null;

    return {
      filterType: 'number',
      type,
      filter: Number(values[0]),
      filterTo: needed === 2 ? Number(values[1]) : null,
    };
  }

  const type = DATE_TYPE_BY_OPERATOR[operator];
  if (!type) return null;
  if (!values[0]) return null;
  if (needed === 2 && !values[1]) return null;

  return {
    filterType: 'date',
    type,
    dateFrom: dayjs(values[0]).format(DATE_FORMAT),
    dateTo: needed === 2 ? dayjs(values[1]).format(DATE_FORMAT) : null,
  };
}

/** Reads an AG Grid column filter model back into an operator plus values for display. */
export function fromAgGridModel(dataType: FilterDataType, model: ReadableFilterModel | null | undefined): ParsedFilter | null {
  if (!model) return null;

  if (dataType === 'boolean') {
    if (!Array.isArray(model.values)) return null;

    const values = model.values.map((value) => (value === null || value === undefined ? EMPTY_FILTER_VALUE : String(value)));
    if (!values.length || values.length >= BOOLEAN_UNIVERSE.length) return null;

    if (values.length === 1) return { operator: 'is', values };

    const excluded = BOOLEAN_UNIVERSE.find((value) => !values.includes(value));
    if (!excluded) return null;

    return { operator: 'is not', values: [excluded] };
  }

  if (dataType === 'text') {
    if (model.filterType === 'text' || model.type === 'equals' || model.type === 'notEqual' || model.type === 'blank' || model.type === 'notBlank' || model.conditions) {
      return fromTextModel(model);
    }

    return fromLegacyTextSetModel(model);
  }

  if (dataType === 'number') {
    const operator = model.type ? NUMBER_OPERATOR_BY_TYPE[model.type] : undefined;
    if (!operator || model.filter === null || model.filter === undefined) return null;

    const values = [String(model.filter)];
    if (model.filterTo !== null && model.filterTo !== undefined) values.push(String(model.filterTo));

    return { operator, values };
  }

  const operator = model.type ? DATE_OPERATOR_BY_TYPE[model.type] : undefined;
  if (!operator || !model.dateFrom) return null;

  const dateFrom = parseFilterDateString(model.dateFrom);
  if (!dateFrom) return null;

  const values = [dateFrom];
  if (model.dateTo) {
    const dateTo = parseFilterDateString(model.dateTo);
    if (dateTo) values.push(dateTo);
  }

  return { operator, values };
}

/** Accepts YYYY-MM-DD, timestamped models, and URL-encoded "+" standing in for spaces. */
export function parseFilterDateString(value: string): string | null {
  const normalized = value.replace(/^(\d{4}-\d{2}-\d{2})\+(\d{2}:\d{2}:\d{2})$/, '$1 $2');
  const parsed = dayjs(normalized);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : null;
}

export function fallbackParsedFilter(dataType: FilterDataType): ParsedFilter {
  return { operator: getDefaultOperator(dataType), values: [] };
}
